import { observer } from "mobx-react";
import _ from "lodash";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "react-bootstrap";
import { ChangeEvent, useCallback, useEffect, useState } from "react";
import { useStores } from "../../stores/root-store";
import { faExpand, faShare } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ApiResponse, IModal } from "../../stores";
import { ShowAlert } from "../../utils/functions";
import { IShareVoucherRequest, IVoucher } from "../../stores/voucher";

interface IShareVoucherForm extends IModal {
  voucher: IVoucher;
}

const ShareVoucherForm = observer(
  ({ show, setShowModal, voucher }: IShareVoucherForm) => {
    const { voucherStore } = useStores();
    const {
      shareVoucherWithCustomers,
      shareVoucherWithAdmin,
      previewVoucherEmail,
    } = voucherStore;

    const [shareRequest, setShareRequest] = useState<IShareVoucherRequest>({
      voucherId: voucher?.voucherId,
      email: false,
      sms: false,
      whatsApp: false,
    });

    const [isFormValid, setIsFormValid] = useState(false);

    useEffect(() => {
      const { email, sms, whatsApp, voucherId } = shareRequest;
      const valid = !!voucherId && (email || sms || whatsApp);
      setIsFormValid(valid);
    }, [shareRequest]);

    const onCheckChange = (evt: ChangeEvent<HTMLInputElement>) => {
      const { checked, id } = evt.target;
      setShareRequest((prev) => ({ ...prev, [id]: checked }));
    };

    const shareCallback = useCallback(
      (res: ApiResponse<boolean>, isAdminRequest: boolean) => {
        if (res && res.isSuccess) {
          setShowModal(false);
          ShowAlert.Success(
            `Voucher shared with ${
              isAdminRequest ? "admin" : "customers"
            } successfully.`
          );
        } else {
          ShowAlert.Error(res?.message ?? "Request failed. Please try again.");
        }
      },
      []
    );

    const handleShareWithCutomers = () => {
      if (!isFormValid) {
        ShowAlert.Error("Select at least one communication type to proceed.");
        return;
      }

      shareVoucherWithCustomers(shareRequest, shareCallback);
    };

    const handleShareWithAdmin = () => {
      const { voucherId } = shareRequest;
      if (!voucherId) {
        ShowAlert.Error(
          "Failed to fetch voucher detail. Please reload the page and try."
        );
        return;
      }

      shareVoucherWithAdmin(voucherId, shareCallback);
    };

    const handlePreviewEmail = () => {
      const { voucherId } = shareRequest;
      if (!voucherId) {
        ShowAlert.Error(
          "Failed to fetch voucher detail. Please reload the page and try."
        );
        return;
      }
      previewVoucherEmail(voucherId);
    };

    return (
      <Modal
        show={show}
        onHide={() => setShowModal(false)}
        className="modal-md"
      >
        <ModalHeader closeButton>
          <Modal.Title>
            <h5>Share Voucher with Customers</h5>
          </Modal.Title>
        </ModalHeader>
        <ModalBody>
          <div className="btn-group btn-group-sm d-flex align-items-center gap-1">
            <button
              className="btn btn-success flex-fill"
              onClick={handlePreviewEmail}
              type="button"
            >
              <FontAwesomeIcon icon={faExpand} className="me-2" />
              <span>Preview Email</span>
            </button>
            <button
              className="btn btn-success flex-fill"
              onClick={handleShareWithAdmin}
              type="button"
            >
              <FontAwesomeIcon icon={faShare} className="me-2" />
              <span>Share with Admin</span>
            </button>
          </div>
          <div className="mt-5">
            <h4 className="text-center">{voucher.voucherCode}</h4>
            <h6>Share via:</h6>
            <div className="d-flex align-items-center border p-3">
              <div className="form-check form-check-inline">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="email"
                  value="1"
                  onChange={onCheckChange}
                />
                <label className="form-check-label fw-bold" htmlFor="email">
                  Email
                </label>
              </div>
              <div className="form-check form-check-inline">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="sms"
                  value="2"
                  onChange={onCheckChange}
                />
                <label className="form-check-label fw-bold" htmlFor="sms">
                  SMS
                </label>
              </div>
              <div className="form-check form-check-inline">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="whatsApp"
                  value="3"
                  onChange={onCheckChange}
                />
                <label className="form-check-label fw-bold" htmlFor="whatsApp">
                  WhatsApp
                </label>
              </div>
            </div>
            <small>* This coupon will be sent to all customers.</small>
          </div>
        </ModalBody>
        <ModalFooter>
          <button
            type="button"
            className="btn btn-default flex-fill"
            onClick={handleShareWithCutomers}
            disabled={!isFormValid}
          >
            <FontAwesomeIcon icon={faShare} className="me-2" />
            <span>Share with Cutomers</span>
          </button>
        </ModalFooter>
      </Modal>
    );
  }
);

export default ShareVoucherForm;
